import React from 'react';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { normalizeStoredFormSchema } from './form-io.util';
import EditIcon from '@mui/icons-material/Edit';
import PreviewIcon from '@mui/icons-material/Preview';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import VisibilityIcon from '@mui/icons-material/Visibility';

export const FORMS_PAGE_SIZE = 10;

export const FORMS_COLUMNS = [
  {
    name: 'name',
    displayName: 'Name',
    sortOrder: 'none',
    isFilterable: true,
    isSortable: true,
    isVisible: true,
    type: 'value',
  },
  {
    name: 'createdBy',
    displayName: 'Created by',
    sortOrder: 'none',
    isFilterable: true,
    isSortable: true,
    isVisible: true,
    type: 'value',
  },
  {
    name: 'active',
    displayName: 'Active',
    sortOrder: 'none',
    isFilterable: true,
    isSortable: true,
    isVisible: true,
    type: 'value',
  },
  {
    name: 'submissionCount',
    displayName: 'Submission count',
    sortOrder: 'none',
    isFilterable: true,
    isSortable: true,
    isVisible: true,
    type: 'value',
  },
];

const NAVY_ICON_SX = { color: 'var(--navy-main)', fontSize: 24 };
const NAVY_ACTION_STYLE = { color: 'var(--navy-main)' };

const getCreatedByFullName = (createdBy) => {
  const first = (createdBy?.firstName ?? '').trim();
  const last = (createdBy?.lastName ?? '').trim();
  return `${first} ${last}`.trim();
};

const getCreatedByUsername = (createdBy) => {
  const username = createdBy?.username;
  if (username == null) {
    return '';
  }
  return String(username).trim();
};

const formatCreatedByDisplay = (form) => {
  const cb = form?.createdBy;
  if (cb == null) {
    return '';
  }
  return getCreatedByFullName(cb) || getCreatedByUsername(cb) || '';
};

const canDisableForm = (form, currentPersonId) =>
  currentPersonId != null &&
  form.personKey != null &&
  Number(form.personKey) === Number(currentPersonId);

const buildDisableAction = (form, onToggleActive) => ({
  title: 'Disable Form',
  onClick: () => onToggleActive(form),
  id: 'toggle-active',
  icon: <ToggleOffIcon sx={NAVY_ICON_SX} />,
  style: NAVY_ACTION_STYLE,
});

const buildEnableAction = (form, onToggleActive) => ({
  title: 'Enable Form',
  onClick: () => onToggleActive(form),
  id: 'toggle-active',
  icon: <ToggleOnIcon sx={NAVY_ICON_SX} />,
  style: NAVY_ACTION_STYLE,
});

const buildViewSubmissionsAction = (form, onViewSubmissions) => ({
  title: 'View Form Submissions',
  onClick: () => onViewSubmissions(form),
  id: 'view-submissions',
  icon: <VisibilityIcon sx={NAVY_ICON_SX} />,
  style: NAVY_ACTION_STYLE,
});

export const buildPreviewFormAction = (form, onPreviewForm) => ({
  title: 'Preview Form',
  onClick: () => onPreviewForm(form),
  id: 'preview-form',
  icon: <PreviewIcon sx={NAVY_ICON_SX} />,
  style: NAVY_ACTION_STYLE,
});

export const buildEditAction = (form, onEditForm) => ({
  title: 'Edit Form',
  onClick: () => onEditForm(form),
  id: 'edit-form',
  icon: <EditIcon sx={NAVY_ICON_SX} />,
  style: NAVY_ACTION_STYLE,
});

export const buildDuplicateFormAction = (form, onDuplicateForm) => ({
  title: 'Duplicate Form',
  onClick: () => onDuplicateForm(form),
  id: 'duplicate-form',
  icon: <ContentCopyIcon sx={NAVY_ICON_SX} />,
  style: NAVY_ACTION_STYLE,
});

export const buildToggleAction = (form, onToggleActive, currentPersonId) => {
  if (!form.active) {
    return buildEnableAction(form, onToggleActive);
  }
  return canDisableForm(form, currentPersonId)
    ? buildDisableAction(form, onToggleActive)
    : null;
};

/**
 * @param {object} form
 * @param {object} handlers
 * @param {number|undefined} currentPersonId — logged-in staff person id (`USER_CONTEXT.personId`)
 * @param {function(object): void} handlers.onToggleActive
 * @param {function(object): void} handlers.onPreviewForm
 * @param {function(object): void} handlers.onViewSubmissions
 * @param {function(object): void} handlers.onEditForm
 * @param {function(object): void} [handlers.onDuplicateForm]
 */
const getFormRowActions = (
  form,
  { onToggleActive, onPreviewForm, onViewSubmissions, onEditForm, onDuplicateForm },
  currentPersonId,
) => {
  const toggleAction = buildToggleAction(form, onToggleActive, currentPersonId);
  const duplicateAction =
    typeof onDuplicateForm === 'function' ? buildDuplicateFormAction(form, onDuplicateForm) : null;
  return [
    buildViewSubmissionsAction(form, onViewSubmissions),
    buildPreviewFormAction(form, onPreviewForm),
    buildEditAction(form, onEditForm),
    ...(duplicateAction ? [duplicateAction] : []),
    ...(toggleAction ? [toggleAction] : []),
  ];
};

/**
 * @param {object[]} forms
 * @param {object} actionHandlers
 * @param {number|undefined} currentPersonId
 * @param {function(object): void} actionHandlers.onToggleActive
 * @param {function(object): void} actionHandlers.onPreviewForm
 * @param {function(object): void} actionHandlers.onViewSubmissions
 * @param {function(object): void} actionHandlers.onEditForm
 * @param {function(object): void} [actionHandlers.onDuplicateForm]
 */
export const mapFormsToGridData = (forms, actionHandlers, currentPersonId) =>
  forms.map((form) => ({
    isSelected: false,
    rowData: {
      name: form.name,
      createdBy: formatCreatedByDisplay(form),
      active: form.active ? 'Yes' : 'No',
      submissionCount: String(form.submissionCount ?? 0),
    },
    preDefineComponentProps: {
      actions: getFormRowActions(form, actionHandlers, currentPersonId),
    },
  }));

const toFormRow = (f) => ({
  id: f.id,
  name: f.name ?? '',
  active: Boolean(f.active),
  submissionCount: typeof f.submissionCount === 'number' ? f.submissionCount : 0,
  personKey: f.personKey,
  createdBy: f.createdBy ?? null,
  schema: f.schema,
});

/** Shape API `/api/content/form` rows for the Forms DataGrid. */
export const mapApiRowsToForms = (rows) => {
  if (!Array.isArray(rows)) {
    return [];
  }
  return rows.map(toFormRow);
};

/** Map GET `/api/content/form/:id` JSON for row actions / previews (same shape as list rows). */
export const mapApiFormDefinitionToForm = (f) => {
  if (f == null) {
    return null;
  }
  return toFormRow(f);
};

const toFormsCsvRow = (form) => [
  form.name ?? '',
  form.active ? 'Yes' : 'No',
  typeof form.submissionCount === 'number' ? String(form.submissionCount) : '0',
];

export const FORMS_CSV_HEADERS = Object.freeze(['Name', 'Active', 'Submission count']);

export const FORMS_CSV_FILENAME = 'forms.csv';

/**
 * Build a CSV export payload (`filename`, `headers`, `rows`) for the Forms grid.
 * @param {readonly { name?: string, active?: boolean, submissionCount?: number }[]} forms
 */
export const buildFormsCsvExportPayload = (forms) => ({
  filename: FORMS_CSV_FILENAME,
  headers: [...FORMS_CSV_HEADERS],
  rows: forms.map(toFormsCsvRow),
});

const DUPLICATE_NAME_PREFIX = 'Copy of ';

/**
 * Builds the display name for a duplicated form (`Copy of {original}`).
 * @param {unknown} sourceName
 * @returns {string}
 */
export const buildDuplicatedFormDisplayName = (sourceName) => {
  const base =
    sourceName != null && String(sourceName).trim() !== ''
      ? String(sourceName).trim()
      : 'Untitled form';
  return `${DUPLICATE_NAME_PREFIX}${base}`;
};

/**
 * Payload for {@link createNewForm} from a GET `/api/content/form/:id` definition.
 * Submissions are not part of the definition and are never copied.
 *
 * @param {{ name?: string, active?: boolean, schema?: unknown } | null | undefined} definition
 * @returns {{ name: string, isActive: boolean, schema: object }}
 */
export const buildDuplicateFormCreatePayload = (definition) => ({
  name: buildDuplicatedFormDisplayName(definition?.name),
  isActive: definition?.active !== false,
  schema: normalizeStoredFormSchema(definition?.schema),
});
